import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import {
  Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Box, IconButton, Tooltip, MenuItem, Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, LinearProgress,
  Alert, Divider, CircularProgress
} from "@mui/material";
import ArrowBackIcon      from "@mui/icons-material/ArrowBack";
import AddIcon            from "@mui/icons-material/Add";
import DeleteIcon         from "@mui/icons-material/Delete";
import EditIcon           from "@mui/icons-material/Edit";
import RocketLaunchIcon   from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import PeopleIcon         from "@mui/icons-material/People";
import { useAuth }        from "../components/AuthContext";

const ESTADO_CONFIG = {
  borrador: { label: "Draft",       color: "default" },
  aprobado: { label: "Approved",    color: "info"    },
  en_curso: { label: "In progress", color: "warning" },
  cerrado:  { label: "Closed",      color: "success" },
};

const ESTADOS_ORDEN = ["borrador", "aprobado", "en_curso", "cerrado"];

export default function PlanDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const puedeEditar = hasPermission("PLANES_EDITAR");

  const [plan, setPlan]           = useState(null);
  const [progreso, setProgreso]   = useState(null);
  const [tab, setTab]             = useState(0);
  const [loading, setLoading]     = useState(true);

  // Catalogs
  const [cursosCat, setCursosCat]   = useState([]);
  const [deptos, setDeptos]         = useState([]);
  const [areas, setAreas]           = useState([]);
  const [puestos, setPuestos]       = useState([]);

  // Dialogs
  const [openEditPlan, setOpenEditPlan]   = useState(false);
  const [editForm, setEditForm]           = useState({});
  const [openAddCurso, setOpenAddCurso]   = useState(false);
  const [newCurso, setNewCurso]           = useState({ id_curso: "", fecha_programada: "", horas_planeadas: "", instructor: "", lugar: "", notas: "" });
  const [openAudiencia, setOpenAudiencia] = useState(false);
  const [audienciaForm, setAudienciaForm] = useState([]);
  const [confirmLanzar, setConfirmLanzar] = useState(false);
  const [lanzandoResult, setLanzandoResult] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/planes/${id}`);
      setPlan(res.data);
      setEditForm({
        nombre:       res.data.nombre,
        anio:         res.data.anio,
        objetivo:     res.data.objetivo || "",
        estado:       res.data.estado,
        fecha_inicio: res.data.fecha_inicio || "",
        fecha_fin:    res.data.fecha_fin    || "",
      });
      setAudienciaForm(res.data.audiencia || []);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const cargarProgreso = useCallback(async () => {
    try {
      const res = await api.get(`/planes/${id}/progreso`);
      setProgreso(res.data);
    } catch { /* plan without audience */ }
  }, [id]);

  useEffect(() => {
    cargar();
    Promise.all([
      api.get("/cursos"),
      api.get("/departamentos"),
      api.get("/areas"),
      api.get("/puestos"),
    ]).then(([c, d, a, p]) => {
      setCursosCat(c.data);
      setDeptos(d.data);
      setAreas(a.data);
      setPuestos(p.data);
    });
  }, [cargar]);

  useEffect(() => {
    if (tab === 2) cargarProgreso();
  }, [tab, cargarProgreso]);

  // ── Edit plan ────────────────────────────────────────────────────────────
  const guardarPlan = async () => {
    try {
      await api.patch(`/planes/${id}`, {
        nombre:       editForm.nombre,
        anio:         parseInt(editForm.anio),
        objetivo:     editForm.objetivo || null,
        estado:       editForm.estado,
        fecha_inicio: editForm.fecha_inicio || null,
        fecha_fin:    editForm.fecha_fin    || null,
      });
      setOpenEditPlan(false);
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  // ── Courses ──────────────────────────────────────────────────────────────
  const agregarCurso = async () => {
    if (!newCurso.id_curso) return alert("Select a course");
    try {
      await api.post(`/planes/${id}/cursos`, {
        id_curso:         parseInt(newCurso.id_curso),
        fecha_programada: newCurso.fecha_programada || null,
        horas_planeadas:  newCurso.horas_planeadas ? parseFloat(newCurso.horas_planeadas) : null,
        instructor:       newCurso.instructor || null,
        lugar:            newCurso.lugar      || null,
        notas:            newCurso.notas      || null,
      });
      setOpenAddCurso(false);
      setNewCurso({ id_curso: "", fecha_programada: "", horas_planeadas: "", instructor: "", lugar: "", notas: "" });
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const quitarCurso = async (id_plan_curso) => {
    if (!window.confirm("Remove this course from the plan?")) return;
    await api.delete(`/planes/${id}/cursos/${id_plan_curso}`);
    cargar();
  };

  // ── Audience ─────────────────────────────────────────────────────────────
  const guardarAudiencia = async () => {
    try {
      await api.put(`/planes/${id}/audiencia`, { items: audienciaForm });
      setOpenAudiencia(false);
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const addAudienciaItem = () => {
    setAudienciaForm(prev => [...prev, { tipo: "departamento", id_referencia: "" }]);
  };

  const removeAudienciaItem = (idx) => {
    setAudienciaForm(prev => prev.filter((_, i) => i !== idx));
  };

  const updateAudienciaItem = (idx, field, val) => {
    setAudienciaForm(prev => prev.map((item, i) =>
      i === idx ? { ...item, [field]: field === "id_referencia" ? (val === "" ? null : parseInt(val)) : val } : item
    ));
  };

  const labelAudiencia = (a) => {
    if (a.tipo === "todos") return "All active employees";
    if (a.tipo === "departamento") {
      const d = deptos.find(x => x.id_departamento === a.id_referencia);
      return d ? `Dept: ${d.nombre_departamento}` : `Dept ID ${a.id_referencia}`;
    }
    if (a.tipo === "area") {
      const ar = areas.find(x => x.id_area === a.id_referencia);
      return ar ? `Area: ${ar.nombre_area}` : `Area ID ${a.id_referencia}`;
    }
    if (a.tipo === "puesto") {
      const pu = puestos.find(x => x.id_puesto === a.id_referencia);
      return pu ? `Position: ${pu.nombre_puesto}` : `Position ID ${a.id_referencia}`;
    }
    return a.tipo;
  };

  // ── Launch ───────────────────────────────────────────────────────────────
  const lanzar = async () => {
    try {
      const res = await api.post(`/planes/${id}/lanzar`);
      setLanzandoResult(res.data);
      setConfirmLanzar(false);
      cargar();
      cargarProgreso();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) return <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>;
  if (!plan) return <Typography sx={{ p: 3 }}>Plan not found</Typography>;

  const cfg = ESTADO_CONFIG[plan.estado] || { label: plan.estado, color: "default" };
  const cursosEnPlanIds = new Set(plan.cursos.map(c => c.id_curso));
  const cursosDisponibles = cursosCat.filter(c => !cursosEnPlanIds.has(c.id_curso));
  const puedeModificar = puedeEditar && plan.estado !== "cerrado";
  const puedeLanzar = puedeEditar && ["borrador", "aprobado"].includes(plan.estado);

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <IconButton onClick={() => navigate("/planes")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{plan.nombre}</Typography>
            <Chip label={cfg.label} color={cfg.color} size="small" />
            <Typography variant="body2" color="text.secondary">{plan.anio}</Typography>
          </Box>
          {plan.objetivo && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {plan.objetivo}
            </Typography>
          )}
          {(plan.fecha_inicio || plan.fecha_fin) && (
            <Typography variant="caption" color="text.secondary">
              {plan.fecha_inicio || "—"} → {plan.fecha_fin || "—"}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {puedeModificar && (
            <Button size="small" startIcon={<EditIcon />} onClick={() => setOpenEditPlan(true)}>
              Edit
            </Button>
          )}
          {puedeLanzar && (
            <Button
              variant="contained" size="small" color="warning"
              startIcon={<RocketLaunchIcon />}
              onClick={() => setConfirmLanzar(true)}
            >
              Launch Plan
            </Button>
          )}
          {puedeEditar && plan.estado === "en_curso" && (
            <Button
              size="small" variant="outlined" color="success"
              startIcon={<CheckCircleIcon />}
              onClick={async () => {
                await api.patch(`/planes/${id}`, { estado: "cerrado" });
                cargar();
              }}
            >
              Close Plan
            </Button>
          )}
        </Box>
      </Box>

      {lanzandoResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLanzandoResult(null)}>
          Plan launched. <strong>{lanzandoResult.asignaciones_creadas}</strong> assignments
          were created for <strong>{lanzandoResult.empleados}</strong> employees.
        </Alert>
      )}

      {/* ── Tabs ── */}
      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          <Tab label={`Courses (${plan.cursos.length})`} />
          <Tab label={`Audience (${plan.audiencia.length})`} />
          <Tab label="Progress" />
        </Tabs>

        {/* ── Tab 0: Courses ── */}
        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            {puedeModificar && (
              <Button size="small" startIcon={<AddIcon />} sx={{ mb: 2 }} onClick={() => setOpenAddCurso(true)}>
                Add course
              </Button>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Scheduled date</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Instructor / Location</TableCell>
                  {puedeModificar && <TableCell></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {plan.cursos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No courses. Add courses to the plan.
                    </TableCell>
                  </TableRow>
                )}
                {plan.cursos.map(pc => (
                  <TableRow key={pc.id_plan_curso} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{pc.nombre_curso}</TableCell>
                    <TableCell>
                      <Chip label={pc.tipo} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{pc.fecha_programada || "—"}</TableCell>
                    <TableCell>{pc.horas_planeadas ?? "—"}</TableCell>
                    <TableCell>
                      {pc.instructor && <Typography variant="body2">{pc.instructor}</Typography>}
                      {pc.lugar      && <Typography variant="caption" color="text.secondary">{pc.lugar}</Typography>}
                      {!pc.instructor && !pc.lugar && "—"}
                    </TableCell>
                    {puedeModificar && (
                      <TableCell>
                        <Tooltip title="Remove from plan">
                          <IconButton size="small" color="error" onClick={() => quitarCurso(pc.id_plan_curso)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* ── Tab 1: Audience ── */}
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Define who this training plan applies to.
              </Typography>
              {puedeModificar && (
                <Button size="small" startIcon={<EditIcon />} onClick={() => {
                  setAudienciaForm(plan.audiencia.map(a => ({ tipo: a.tipo, id_referencia: a.id_referencia ?? "" })));
                  setOpenAudiencia(true);
                }}>
                  Edit audience
                </Button>
              )}
            </Box>
            {plan.audiencia.length === 0 ? (
              <Alert severity="warning">No audience has been defined. The plan cannot be launched.</Alert>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {plan.audiencia.map((a, i) => (
                  <Chip key={i} icon={<PeopleIcon />} label={labelAudiencia(a)} variant="outlined" />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── Tab 2: Progress ── */}
        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            {!progreso ? (
              <Alert severity="info">Define the audience and launch the plan to see progress.</Alert>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Employees in audience: <strong>{progreso.total_empleados}</strong>
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Course</TableCell>
                      <TableCell>Sched. date</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Progress</TableCell>
                      <TableCell>Passed</TableCell>
                      <TableCell>Pending</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {progreso.cursos.map(c => (
                      <TableRow key={c.id_plan_curso} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{c.nombre_curso}</TableCell>
                        <TableCell>{c.fecha_programada || "—"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={c.porcentaje}
                              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                              color={c.porcentaje === 100 ? "success" : "primary"}
                            />
                            <Typography variant="caption" sx={{ minWidth: 36 }}>
                              {c.porcentaje}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={c.aprobados} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={c.pendientes} color={c.pendientes > 0 ? "warning" : "default"} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* ── Dialog: Edit Plan ── */}
      <Dialog open={openEditPlan} onClose={() => setOpenEditPlan(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Plan</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="Name *" fullWidth value={editForm.nombre || ""}
            onChange={e => setEditForm({ ...editForm, nombre: e.target.value })} />
          <TextField label="Year *" type="number" fullWidth value={editForm.anio || ""}
            onChange={e => setEditForm({ ...editForm, anio: e.target.value })}
            inputProps={{ min: 2000, max: 2100 }} />
          <TextField label="Objective" fullWidth multiline rows={3} value={editForm.objetivo || ""}
            onChange={e => setEditForm({ ...editForm, objetivo: e.target.value })} />
          <TextField select label="Status" fullWidth value={editForm.estado || ""}
            onChange={e => setEditForm({ ...editForm, estado: e.target.value })}>
            {ESTADOS_ORDEN.map(s => (
              <MenuItem key={s} value={s}>{ESTADO_CONFIG[s]?.label || s}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Start date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={editForm.fecha_inicio || ""}
              onChange={e => setEditForm({ ...editForm, fecha_inicio: e.target.value })} />
            <TextField label="End date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={editForm.fecha_fin || ""}
              onChange={e => setEditForm({ ...editForm, fecha_fin: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditPlan(false)}>Cancel</Button>
          <Button variant="contained" onClick={guardarPlan}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Add Course ── */}
      <Dialog open={openAddCurso} onClose={() => setOpenAddCurso(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Course to Plan</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField select label="Course *" fullWidth value={newCurso.id_curso}
            onChange={e => setNewCurso({ ...newCurso, id_curso: e.target.value })}>
            <MenuItem value="">— Select —</MenuItem>
            {cursosDisponibles.map(c => (
              <MenuItem key={c.id_curso} value={c.id_curso}>
                {c.nombre_curso} ({c.tipo})
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Scheduled date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={newCurso.fecha_programada}
              onChange={e => setNewCurso({ ...newCurso, fecha_programada: e.target.value })} />
            <TextField label="Planned hours" type="number" fullWidth
              value={newCurso.horas_planeadas}
              onChange={e => setNewCurso({ ...newCurso, horas_planeadas: e.target.value })}
              inputProps={{ step: 0.5, min: 0 }} />
          </Box>
          <TextField label="Instructor" fullWidth value={newCurso.instructor}
            onChange={e => setNewCurso({ ...newCurso, instructor: e.target.value })} />
          <TextField label="Location" fullWidth value={newCurso.lugar}
            onChange={e => setNewCurso({ ...newCurso, lugar: e.target.value })} />
          <TextField label="Notes" fullWidth multiline rows={2} value={newCurso.notas}
            onChange={e => setNewCurso({ ...newCurso, notas: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddCurso(false)}>Cancel</Button>
          <Button variant="contained" onClick={agregarCurso}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Audience ── */}
      <Dialog open={openAudiencia} onClose={() => setOpenAudiencia(false)} fullWidth maxWidth="sm">
        <DialogTitle>Define Audience</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define which employees this plan applies to. You can combine several criteria (the union of all of them).
          </Typography>
          {audienciaForm.map((item, idx) => (
            <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
              <TextField select label="Type" size="small" value={item.tipo}
                onChange={e => updateAudienciaItem(idx, "tipo", e.target.value)}
                sx={{ minWidth: 150 }}>
                <MenuItem value="todos">Everyone</MenuItem>
                <MenuItem value="departamento">Department</MenuItem>
                <MenuItem value="area">Area</MenuItem>
                <MenuItem value="puesto">Position</MenuItem>
              </TextField>
              {item.tipo !== "todos" && (
                <TextField select label="Select" size="small" value={item.id_referencia ?? ""}
                  onChange={e => updateAudienciaItem(idx, "id_referencia", e.target.value)}
                  sx={{ flexGrow: 1 }}>
                  <MenuItem value="">— Select —</MenuItem>
                  {item.tipo === "departamento" && deptos.map(d => (
                    <MenuItem key={d.id_departamento} value={d.id_departamento}>{d.nombre_departamento}</MenuItem>
                  ))}
                  {item.tipo === "area" && areas.map(a => (
                    <MenuItem key={a.id_area} value={a.id_area}>{a.nombre_area}</MenuItem>
                  ))}
                  {item.tipo === "puesto" && puestos.map(p => (
                    <MenuItem key={p.id_puesto} value={p.id_puesto}>{p.nombre_puesto}</MenuItem>
                  ))}
                </TextField>
              )}
              {item.tipo === "todos" && <Box sx={{ flexGrow: 1 }} />}
              <IconButton size="small" color="error" onClick={() => removeAudienciaItem(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addAudienciaItem} sx={{ mt: 1 }}>
            Add criterion
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAudiencia(false)}>Cancel</Button>
          <Button variant="contained" onClick={guardarAudiencia}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Launch ── */}
      <Dialog open={confirmLanzar} onClose={() => setConfirmLanzar(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Launch Training Plan</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Launching the plan will create assignments in <strong>Courses → Employees</strong> for all
            employees in the defined audience. Those who already have the course assigned will not be duplicated.
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Courses in the plan: <strong>{plan.cursos.length}</strong> &nbsp;·&nbsp;
            Audience: <strong>{plan.audiencia.length > 0 ? `${plan.audiencia.length} criterion(s)` : "Not defined"}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLanzar(false)}>Cancel</Button>
          <Button variant="contained" color="warning" startIcon={<RocketLaunchIcon />} onClick={lanzar}>
            Launch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
